/**
 * URL Normalizer Utility
 *
 * Automatically normalizes URLs using Angular Router's route configuration.
 * Extracts route parameters and their values, replacing them with their parameter names
 * to create normalized URLs for analytics.
 *
 * This approach is fully generic - no manual route configuration needed!
 */

import { ActivatedRouteSnapshot } from '@angular/router';
import { extractRouteParams } from '@campus/utils';

export interface NormalizedUrl {
  /** Original raw URL */
  rawUrl: string;

  /** Normalized URL with parameter names (e.g., /tasks/:taskId?tab=:tab) */
  normalizedUrl: string;

  /** Extracted route parameters with their values */
  routeParams: Record<string, string>;

  /** Query parameters (if any) */
  queryParams: Record<string, string>;
}

/**
 * Simple LRU cache for normalized URLs
 * Caches based on the combination of URL + route snapshot identity
 */
class NormalizationCache {
  private cache = new Map<string, NormalizedUrl>();
  private readonly maxSize = 100; // Keep last 100 normalized URLs

  get(key: string): NormalizedUrl | undefined {
    return this.cache.get(key);
  }

  set(key: string, value: NormalizedUrl): void {
    // Simple LRU: if cache is full, delete the oldest entry
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
  }
}

// Global cache instance
const normalizationCache = new NormalizationCache();

/**
 * Generate a cache key from URL and route snapshot
 * Uses the route snapshot's path configuration as a stable identifier
 */
function generateCacheKey(url: string, routeSnapshot: ActivatedRouteSnapshot): string {
  // Build a stable key from the route configuration path
  const routeChain: string[] = [];
  let tempRoute: ActivatedRouteSnapshot | null = routeSnapshot;

  while (tempRoute) {
    // Use the route config path if available (e.g., ':book', 'methods', etc.)
    if (tempRoute.routeConfig?.path) {
      routeChain.unshift(tempRoute.routeConfig.path);
    }
    tempRoute = tempRoute.parent;
  }

  const routeKey = routeChain.join('/');
  return `${routeKey}|${url}`;
}

/**
 * Parse query string into key-value pairs
 */
function parseQueryParams(queryString?: string): Record<string, string> {
  if (!queryString) {
    return {};
  }

  const params: Record<string, string> = {};
  const pairs = queryString.split('&');

  for (const pair of pairs) {
    const [key, value] = pair.split('=');
    if (key) {
      params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
    }
  }

  return params;
}

/**
 * Build normalized query string with parameter names instead of values
 *
 * @param queryParams - Object containing query parameters
 * @returns Normalized query string (e.g., "?tab=:tab&filter=:filter")
 *
 * @example
 * buildNormalizedQueryString({ tab: 'overview', filter: 'active' })
 * // Returns: "?tab=:tab&filter=:filter"
 */
function buildNormalizedQueryString(queryParams: Record<string, string>): string {
  const keys = Object.keys(queryParams);

  if (keys.length === 0) {
    return '';
  }

  const normalizedPairs = keys
    .sort() // Sort for consistent output
    .map((key) => `${encodeURIComponent(key)}=:${key}`);

  return '?' + normalizedPairs.join('&');
}

/**
 * Build the normalized path from a route snapshot by traversing the route tree.
 * Replaces parameter values with their names prefixed by colon.
 *
 * @param route - The activated route snapshot
 * @returns The normalized path with parameter names instead of values
 *
 * @example
 * URL: /methods/book-123/toc-456
 * Route config: path: 'methods/:bookId/:tocId'
 * Returns: /methods/:bookId/:tocId
 */
function buildNormalizedPath(route: ActivatedRouteSnapshot | null): string {
  if (!route) {
    return '';
  }

  const segments: string[] = [];

  // Start from the root and traverse the entire route tree
  // We need to go up to the root first, then traverse down
  const routeChain: ActivatedRouteSnapshot[] = [];
  let tempRoute: ActivatedRouteSnapshot | null = route;

  // Build the chain from current route up to root
  while (tempRoute) {
    routeChain.unshift(tempRoute); // Add to beginning
    tempRoute = tempRoute.parent;
  }

  // Now traverse the chain and build the path
  for (const routeSnapshot of routeChain) {
    // Process each URL segment at this route level
    routeSnapshot.url.forEach((segment) => {
      // Check if this segment value matches any parameter value in the current route's params
      const paramKey = Object.keys(routeSnapshot.params).find((key) => routeSnapshot.params[key] === segment.path);

      if (paramKey) {
        // This segment is a parameter value, replace with parameter name
        segments.push(`:${paramKey}`);
      } else {
        // This is a static segment, keep as-is
        segments.push(segment.path);
      }
    });
  }

  return '/' + segments.filter((s) => s).join('/');
}

/**
 * Normalize a URL using Angular's route information.
 *
 * This function leverages Angular Router's ActivatedRouteSnapshot to automatically
 * determine which URL segments are parameters and which are static paths.
 *
 * @param url - The raw URL to normalize
 * @param routeSnapshot - The activated route snapshot (should be provided for accurate normalization)
 * @returns Normalized URL with extracted parameters
 *
 * @example
 * ```typescript
 * // In a component or service with access to ActivatedRoute:
 * const snapshot = this.activatedRoute.snapshot;
 * normalizeUrl('/methods/book-123/toc-456', snapshot);
 * // Returns: {
 * //   rawUrl: '/methods/book-123/toc-456',
 * //   normalizedUrl: '/methods/:bookId/:tocId',
 * //   routeParams: { bookId: 'book-123', tocId: 'toc-456' },
 * //   queryParams: {}
 * // }
 *
 * // With query params:
 * normalizeUrl('/methods/book-123/toc-456?tab=overview&filter=active', snapshot);
 * // Returns: {
 * //   rawUrl: '/methods/book-123/toc-456',
 * //   normalizedUrl: '/methods/:bookId/:tocId?filter=:filter&tab=:tab',
 * //   routeParams: { bookId: 'book-123', tocId: 'toc-456' },
 * //   queryParams: { tab: 'overview', filter: 'active' }
 * // }
 * ```
 */
export function normalizeUrl(url: string, routeSnapshot?: ActivatedRouteSnapshot | null): NormalizedUrl {
  if (!url) {
    return {
      rawUrl: '',
      normalizedUrl: '',
      routeParams: {},
      queryParams: {},
    };
  }

  // Split URL and query string
  const [pathPart, queryPart] = url.split('?');
  const queryParams = parseQueryParams(queryPart);

  // If we have route snapshot, use it for accurate normalization
  if (routeSnapshot) {
    // Check cache first
    const cacheKey = generateCacheKey(pathPart, routeSnapshot);
    const cached = normalizationCache.get(cacheKey);

    if (cached) {
      // Return cached result with updated query params (query params can change frequently)
      const normalizedQueryString = buildNormalizedQueryString(queryParams);
      return {
        ...cached,
        normalizedUrl: cached.normalizedUrl.split('?')[0] + normalizedQueryString,
        queryParams,
      };
    }

    // Not in cache, compute it
    const normalizedPath = buildNormalizedPath(routeSnapshot);
    const routeParams = extractRouteParams(routeSnapshot);
    const normalizedQueryString = buildNormalizedQueryString(queryParams);

    const result: NormalizedUrl = {
      rawUrl: pathPart,
      normalizedUrl: (normalizedPath || pathPart) + normalizedQueryString,
      routeParams,
      queryParams,
    };

    // Store in cache (without query params as they change frequently)
    normalizationCache.set(cacheKey, {
      ...result,
      queryParams: {}, // Don't cache query params
    });

    return result;
  }

  // Fallback: no route snapshot available
  // Return the URL as-is since we can't determine parameter names without Angular's route config
  return {
    rawUrl: pathPart,
    normalizedUrl: pathPart,
    routeParams: {},
    queryParams,
  };
}

/**
 * Get normalized page name for analytics.
 * Convenience function that just returns the normalized URL string.
 *
 * @param url - The raw URL
 * @param routeSnapshot - The activated route snapshot
 * @returns Just the normalized URL string
 */
export function getNormalizedPage(url: string, routeSnapshot?: ActivatedRouteSnapshot | null): string {
  return normalizeUrl(url, routeSnapshot).normalizedUrl;
}

/**
 * Check if a URL has route parameters
 *
 * @param url - The raw URL
 * @param routeSnapshot - The activated route snapshot
 * @returns True if the route has parameters
 */
export function hasRouteParams(url: string, routeSnapshot?: ActivatedRouteSnapshot | null): boolean {
  const normalized = normalizeUrl(url, routeSnapshot);
  return Object.keys(normalized.routeParams).length > 0;
}
